// Avatar URL resolver for OG share cards. Honours the per-user
// `avatarPreferences.rasterise` setting:
//
//   - `rasterise === true`  → render through the pattern-studio rasteriser
//                             (`/api/pattern/[hash].png`), same as before.
//   - `rasterise === false` → return the raw uploaded URL rewritten through
//                             the custom Pinata gateway (`images.morfi.markets`)
//                             so OG fetchers count against our paid plan.
//
// When the user has no upload (`avatarUrl` is null/empty) the rasteriser is
// always used — there's nothing to passthrough, and the rasteriser produces
// the deterministic initials placeholder that's the right fallback.
//
// Mirrors the in-app decision logic in `<OptimizedImage>` (see
// `isPassthroughEntity` for users) so OG cards stay visually consistent
// with what the user sees in the app.

import { toCustomGateway } from '@morfi/backend-sdk-v2/server';
import type { PatternManifest } from '@/lib/pattern-studio';

import { type GetPatternUrlOptions, getPatternUrl } from './get-pattern-url';

export interface GetAvatarOgUrlArgs {
  /** Raw uploaded avatar URL or `null` when the user has no upload. */
  readonly avatarUrl: string | null;
  /**
   * Per-user `avatarPreferences.rasterise`. `true` (rasterise) is the
   * default for new users; existing users were backfilled to `false`.
   */
  readonly rasterise: boolean;
  /**
   * Pattern manifest for the rasterised render — used when either
   * `rasterise === true` OR `avatarUrl` is null/empty. Build with
   * `manifestForUser(...)` per existing OG-route convention.
   */
  readonly manifest: PatternManifest;
}

/**
 * Resolve the URL to embed in `<OgImage src=...>` for a user avatar,
 * respecting their `avatarPreferences.rasterise` setting.
 */
export function getAvatarOgUrl(
  args: GetAvatarOgUrlArgs,
  options: GetPatternUrlOptions = {},
): string {
  if (args.rasterise === false && typeof args.avatarUrl === 'string' && args.avatarUrl.length > 0) {
    return toCustomGateway(args.avatarUrl);
  }
  return getPatternUrl(args.manifest, options);
}
